import axios from 'axios';

declare global {
    interface Window {
        profile?: {
            user?: any;
            agency?: any;
            modules?: Record<string, boolean>;
            actions?: Record<string, boolean>;
            featureFlags?: Record<string, boolean>;
            isAgencyUser?: boolean;
            isSuperAgencyUser?: boolean;
            isSuperUser?: boolean;
            persist?: boolean;
            sId?: string;
        };
        featureFlagCache?: {
            isCacheEnabled: () => boolean;
            clear: () => void;
            getProfileFlags: () => Record<string, boolean> | null;
        };
    }
}

export enum FeatureFlagId {
    ActionsDigestNotifications = 'actions-digest',
    ActionsPerformanceRate = 'actions-performance-rate',
    ActionResponseChaining = 'actions-response-chaining',
    AIReputationManagerPresenceDashboard = 'ai-reputation-manager-presence-dashboard',
    AssetLibrary = 'asset-library',
    AdminCustomerContacts = 'admin-customer-contacts',
    AdminSavedViews = 'admin-saved-views',
    AutofindProgress = 'autofind-progress',
    AnalyticVOBConfiguration = 'vob-configuration',
    CompetitiveInsightsV3 = 'competitive-insights-V3',
    Credentials2 = 'credentials2',
    DashboardFacetExpand = 'dashboard-facet-expand',
    ExperienceInsightsEnableSurveyTemplateVariables = 'experience-insights-enable-survey-template-variables',
    ExperienceActionPlans = 'experience-action-plans',
    ExperienceLegacyLocationLeaderboard = 'experience-legacy-location-leaderboard',
    FullScreen = 'full-screen',
    GoogleAgencyUser = 'google-agency-user',
    InboxAudioRecorder = 'inbox-audio-recorder',
    LegacyReviewNotifications = 'legacy-reviews-notifs',
    LocationModules = 'location-module-source-rules',
    ListingsAuditScreen = 'listings-audit-screen',
    EnableImportSourceValidations = 'enable-import-source-validations',
    ProfileIndexWithAuditData = 'ProfileIndexWithAuditData',
    ListingsCategoryMapping1Retained = 'listings-category-mapping1-retained',
    ListingsDuplicateReact = 'listings-duplicates-react',
    ListingsSuggestionsReact = 'listings-suggestions-react',
    ListingsLocator2 = 'listings-locators-2',
    ListingsPages2 = 'listings-pages-2',
    ProfileAdditionalReach = 'profile_additional_reach',
    ListingsProducts = 'listings-products',
    ListingsViewProfileReact = 'listings-view-profile-react',
    ListingsDisableImageResizer = 'listings-disable-image-resizer',
    Looker = 'looker',
    ManualReviewResponse = 'manual-review-response',
    ObjectPrivileges = 'object-privileges',
    ProfileImportExport = 'profile_import_export',
    R4ESaml = 'r4e-saml',
    RepConnectMaxRollingDays = 'rep-connect-survey-exporter-max-days',
    Reports2 = 'reports-2',
    ReputationScore = 'rep-score-x-react',
    ReputationScoreBrandComponents = 'rsx-brand-components',
    ReputationScoreMessagingComponents = 'rsx-messaging-components',
    ReputationScoreSplitListing = 'rsx-split-listings',
    RSImpactReport = 'rep-score-impact-report-2024',
    SurveyNotifications = 'survey-notifications',
    Surveys3HidePracticeOfficeFilter = 'surveys3-hide-practice-office-filter',
    Surveys3TenantDefaultSurveyView = 'surveys3-tenant-default-survey-view',
    FixSourceValidationsTicket = 'fix-source-validations-ticket',
    DataCleansingTicket = 'data-cleansing-ticket',
    RespondWidget = 'respond-widget',
    Surveys3AllSurveysV2 = 'surveys3-all-surveys-v2',
    Surveys3IgnoreHiddenQuestionAnswers = 'surveys3-ignore-hidden-question-answers',
    Surveys3ResultsFeedV2 = 'surveys3-results-feed-v2',
    Surveys3ResultsBetaRollout = 'surveys3-results-feed-beta-rollout',
    Surveys3ResultsFeedEmployeeWow = 'surveys3-results-feed-employee-wow',
    SurveysComment2Setup = 'surveys-comment2-setup',
    SurveyResponseChaining = 'survey-response-chaining',
    SurveysEmailResponseChaining = 'surveys-email-response-chaining',
    SocialInbox = 'social-inbox',
    InboxDueDate = 'inbox-due-date',
    ProfilesCustomCategories = 'profiles-custom-categories',
    TwitterDMLink = 'twitter-dm-link',
    InboxMetrics2 = 'inbox-metrics2',
    InboxMentionsAdPosts = 'inbox-mentions-adpost',
    ListingsCompetitiveInsights = 'listings-competitive-insights',
    ListingsPerformanceInsights = 'listings-performance-react',
    HideListingDetailsAudit = 'hide-listings-details-audit',
    ResourcesLinks = 'resources-links',
    SocialHidePublishAssistant = 'social-hide-publish-assistant',
    Surveys3ResultsFeedV2CustomerPanel = 'surveys-RFV2-customer-panel',
    RichContentImageSupport = 'rich-content-image-support',
    SocialCustomizeBySource = 'social-customize-by-source',
    SocialSmartSocial = 'social-smart-social',
    InboxMessageMenu = 'inbox-message-menu',
    ResultsFeedV2HiChart = 'surveys-RFV2-KPIgauge',
    PrivacySettingsOptIn = 'privacy-settings-opt-in',
    Reviews3 = 'reviews3',
    Review2Inbox = 'review2-inbox', // use Review(2) component in inbox
    InboxReviews = 'reviews_in_inbox',
    InboxActionsAutomation = 'inbox-actions-automation',
    ExperienceEnableAIReputationManagerTab = 'experience-enable-ai-reputation-manager-tab',
    NovaCommandCenter = 'nova-command-center',
    Surveys3VideoSurveyQuestion = 'surveys3-video-survey-question',
    ActionPlansReact = 'action-plans-react',
    TenantActionsReact = 'tenant-actions-react',
    EnableRespondExplainAndClose = 'enable-response-explain-and-close',
    LookerInsightsPoc = 'looker-insights-poc',
    AISuggestedResponse = 'ai-suggested-response',
    SelfServiceAutomation = 'self-service-automation',
    ActionPlansApproval = 'action-plans-approval',
    SavedViewsFilters = 'saved-views-filters',
    LocationMatchingUrls = 'location-matching-urls',
    TenantLocationsReact = 'tenant-locations-react',
    SurveysInInbox = 'Survey-In-Inbox',
    Reviews3BulkActions = 'reviews3-bulk-actions',
    Reviews3YetiDataFetcher = 'review-yeti-data-fetcher',
    Surveys3YetiDataFetcher = 'survey-yeti-data-fetcher',
    SurveysRFV2CompleteMigration = 'surveys-rfv2-complete-migration',
    SocialNuviCreatePost = 'social-nuvi-create-post',
    DeprecatedInsights = 'deprecated-insights',
    Reviews3Notification = 'reviews3-notification-enabled',
    TenantRolesReact = 'tenant-roles2',
    EnableBulkUpload = 'enable-bulk-upload',
    ActionsFieldSearch = 'actions-field-search',
    ActionsFieldSearchExcludeDateFilter = 'actions-field-search-exclude-date-filter',
    HIDE_WEB_CHAT = 'hide_web_chat',
    ReviewYetiDataFetcher = 'review-yeti-data-fetcher',
    SurveyResultCloning = 'surveys-cloning',
    TenantActionsYetiMigration = 'tenant-actions-yeti-migration',
    Survey3NotificationAndAutomation = 'survey3-notification-and-automation',
    Survey3CoreYetiResponse = 'survey-response-yeti',
    EnableKeywordSearch = 'react-enable-keyword-search',
    DeleteUser = 'delete-user',
    EscalateModerateReviews = 'escalate-moderate-reviews',
    EnableProductBoard = 'enable-product-board',
    EnableVOBActions = 'enable-vob-actions',
    ListingLocatorsFiltersSearchYeti = 'listing-locators-filter-search-yeti',
    FacebookPagesNonLocations = 'facebook-pages-non-locations',
    RepIQ = 'rep-iq',
    SurveyBuilderAngular = 'enable-angular-builder',
    ProposeReviewResponseTicket = 'propose-responses',
    EnableUpdateEventsForTicketCreation = 'enable-update-events-for-ticket-creation',
    KioskSurveyMigration = 'kiosk-survey-migration',
    EnableProactNotifications = 'enable-proact-notifications',
    Reviews3NoResponseRequired = 'reviews3-no-response-required',
    Reviews3NorthStar = 'reviews3-north-star',
    SurveysHideOverallCommentTranslation = 'surveys-hide-overall-comment-translation',
}

export interface FeatureFlagStatus {
    enabled: boolean;
}

export interface MultipleFeatureFlagResponse {
    flagStatuses: { id: string; enabled: boolean }[];
    response: any;
}

interface CachedFlag {
    value: boolean;
    timestamp: number;
    ttl: number;
}

// Calculate once at module level
const SHOW_LOGS = process.env.REACT_APP_FF_LOGS === 'true';

// Helper to access window.profile.featureFlags safely
const getProfileFeatureFlags = (): Record<string, boolean> | null => {
    try {
        if (typeof window !== 'undefined' && window.profile?.featureFlags) {
            return window.profile.featureFlags;
        }
    } catch (error) {
        // Silently fail if window.profile is not accessible
    }
    return null;
};

// Helper to check window.profile.featureFlags
const checkProfileCache = (featureFlagId: string): boolean | null => {
    const profileFlags = getProfileFeatureFlags();

    if (profileFlags && featureFlagId in profileFlags) {
        return profileFlags[featureFlagId];
    }
    return null;
};

class FeatureFlagCache {
    private readonly CACHE_PREFIX = 'ff_cache_';

    private readonly DEFAULT_TTL = 20 * 60 * 1000; // 20 minutes

    private readonly CACHE_ENABLED = true; // Set to false to disable caching

    private static getFromLocalStorage(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch (error) {
            console.warn('Feature flag cache: localStorage not available', error);
            return null;
        }
    }

    private setToLocalStorage(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            console.warn('Feature flag cache: Failed to write to localStorage', error);
            this.cleanupExpiredEntries();
        }
    }

    private cleanupExpiredEntries(): void {
        if (typeof localStorage === 'undefined') return;

        const now = Date.now();
        const keysToRemove: string[] = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);

            if (key?.startsWith(this.CACHE_PREFIX)) {
                try {
                    const stored = FeatureFlagCache.getFromLocalStorage(key);

                    if (stored) {
                        const cached: CachedFlag = JSON.parse(stored);

                        if (now - cached.timestamp > cached.ttl) {
                            keysToRemove.push(key);
                        }
                    }
                } catch (error) {
                    keysToRemove.push(key);
                }
            }
        }

        keysToRemove.forEach((key) => {
            try {
                localStorage.removeItem(key);
            } catch (error) {
                // Ignore errors during cleanup
            }
        });
    }

    get(cacheKey: string): boolean | null {
        if (!this.CACHE_ENABLED) {
            return null;
        }

        const fullKey = this.CACHE_PREFIX + cacheKey;
        const stored = FeatureFlagCache.getFromLocalStorage(fullKey);

        if (stored) {
            try {
                const cached: CachedFlag = JSON.parse(stored);
                const now = Date.now();

                if (now - cached.timestamp < cached.ttl) {
                    return cached.value;
                }

                try {
                    localStorage.removeItem(fullKey);
                } catch (error) {
                    // Ignore cleanup errors
                }
            } catch (error) {
                console.warn('Feature flag cache: Invalid cached data for key', cacheKey, error);
                try {
                    localStorage.removeItem(fullKey);
                } catch (cleanupError) {
                    // Ignore cleanup errors
                }
            }
        }

        return null;
    }

    set(cacheKey: string, value: boolean, ttl?: number): void {
        if (!this.CACHE_ENABLED) return;

        try {
            const cached: CachedFlag = {
                value,
                timestamp: Date.now(),
                ttl: ttl || this.DEFAULT_TTL,
            };

            const fullKey = this.CACHE_PREFIX + cacheKey;

            this.setToLocalStorage(fullKey, JSON.stringify(cached));
        } catch (error) {
            console.warn('Feature flag cache: Failed to cache flag', cacheKey, error);
        }
    }

    public isCacheEnabled(): boolean {
        return this.CACHE_ENABLED;
    }

    public clear(): void {
        if (typeof localStorage === 'undefined') return;

        const keysToRemove: string[] = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);

            if (key?.startsWith(this.CACHE_PREFIX)) {
                keysToRemove.push(key);
            }
        }

        keysToRemove.forEach((key) => {
            try {
                localStorage.removeItem(key);
            } catch (error) {
                console.warn('Failed to remove cache key', key, error);
            }
        });

        if (SHOW_LOGS) {
            console.log('Feature flag cache cleared');
        }
    }
}

// Create cache instance
const cache = new FeatureFlagCache();

export const isFeatureFlagEnabled = async (
    tenantId: number,
    featureFlagId: string,
    options: { forceRefresh?: boolean; cacheTTL?: number } = {},
): Promise<FeatureFlagStatus> => {
    if (!options.forceRefresh) {
        // Check memory cache first (window.profile.featureFlags)
        const profileValue = checkProfileCache(featureFlagId);

        if (profileValue !== null) {
            if (SHOW_LOGS) {
                console.log(`Feature Flag PROFILE: ${featureFlagId} = ${profileValue}`);
            }
            return { enabled: profileValue };
        }

        // Check localStorage cache second
        const cacheKey = `${tenantId}_${featureFlagId}`;
        const cachedValue = cache.get(cacheKey);

        if (cachedValue !== null) {
            if (SHOW_LOGS) {
                console.log(`Feature Flag CACHED: ${featureFlagId} = ${cachedValue}`);
            }
            return { enabled: cachedValue };
        }
    }

    // Make API call as last resort
    try {
        const cacheKey = `${tenantId}_${featureFlagId}`;
        const { data } = await axios.get<FeatureFlagStatus>(
            `/api/tenants/${tenantId}/feature-flags/${featureFlagId}/is-enabled`,
        );

        cache.set(cacheKey, data.enabled, options.cacheTTL);

        if (SHOW_LOGS) {
            console.log(`Feature Flag API: ${featureFlagId} = ${data.enabled}`);
        }

        return data;
    } catch (error) {
        if (SHOW_LOGS) {
            console.error(`Feature Flag API Error: ${featureFlagId}`, error);
        }
        throw error;
    }
};

export const isFeatureFlagEnabledWithAgencyId = async (
    tenantId: number,
    featureFlagId: string,
    agencyId: string,
    options: { forceRefresh?: boolean; cacheTTL?: number } = {},
): Promise<FeatureFlagStatus> => {
    // Note: window.profile.featureFlags doesn't have agency-specific flags,
    // so we skip the profile check and go straight to localStorage cache

    const cacheKey = `${tenantId}_${featureFlagId}_${agencyId}`;

    if (!options.forceRefresh) {
        const cachedValue = cache.get(cacheKey);

        if (cachedValue !== null) {
            if (SHOW_LOGS) {
                console.log(`Feature Flag CACHED (agency): ${featureFlagId} = ${cachedValue}`);
            }
            return { enabled: cachedValue };
        }
    }

    try {
        const { data } = await axios.get<FeatureFlagStatus>(
            `/api/tenants/${tenantId}/feature-flags/${featureFlagId}/is-enabled?agencyId=${agencyId}`,
        );

        cache.set(cacheKey, data.enabled, options.cacheTTL);

        if (SHOW_LOGS) {
            console.log(`Feature Flag API (agency): ${featureFlagId} = ${data.enabled}`);
        }

        return data;
    } catch (error) {
        if (SHOW_LOGS) {
            console.error(`Feature Flag API Error (agency): ${featureFlagId}`, error);
        }
        throw error;
    }
};

export const areFeatureFlagsEnabled = async (
    tenantId: number,
    featureFlags: string[],
    options: { forceRefresh?: boolean; cacheTTL?: number } = {},
): Promise<MultipleFeatureFlagResponse> => {
    const cachedResults: { id: string; enabled: boolean }[] = [];
    const flagsToFetch: string[] = [];

    if (!options.forceRefresh) {
        featureFlags.forEach((flagId) => {
            // Check memory cache first (window.profile.featureFlags)
            const profileValue = checkProfileCache(flagId);

            if (profileValue !== null) {
                cachedResults.push({ id: flagId, enabled: profileValue });
                return;
            }

            // Check localStorage cache second
            const cacheKey = `${tenantId}_${flagId}`;
            const cachedValue = cache.get(cacheKey);

            if (cachedValue !== null) {
                cachedResults.push({ id: flagId, enabled: cachedValue });
            } else {
                flagsToFetch.push(flagId);
            }
        });
    } else {
        flagsToFetch.push(...featureFlags);
    }

    if (SHOW_LOGS) {
        console.log(`Bulk Feature Flags - Cached: ${cachedResults.length}, API: ${flagsToFetch.length}`);
    }

    if (flagsToFetch.length === 0) {
        if (SHOW_LOGS) {
            console.log('All bulk feature flags cached');
        }

        return {
            flagStatuses: cachedResults,
            response: null,
        };
    }

    try {
        const { data } = await axios.post<MultipleFeatureFlagResponse>(
            `/api/tenants/${tenantId}/feature-flags/is-enabled`,
            { featureFlags: flagsToFetch },
        );

        data.flagStatuses.forEach((flag) => {
            const cacheKey = `${tenantId}_${flag.id}`;

            cache.set(cacheKey, flag.enabled, options.cacheTTL);
        });

        if (SHOW_LOGS) {
            console.log(`Bulk Feature Flags API: ${flagsToFetch.length} flags`);
        }

        const combinedResults = [...cachedResults, ...data.flagStatuses];

        return {
            flagStatuses: combinedResults,
            response: data.response,
        };
    } catch (error) {
        if (SHOW_LOGS) {
            console.error('Bulk Feature Flags API Error', error);
        }
        throw error;
    }
};

// Utility for debugging
if (typeof window !== 'undefined') {
    (window as any).featureFlagCache = {
        isCacheEnabled: () => cache.isCacheEnabled(),
        clear: () => cache.clear(),
        getProfileFlags: () => getProfileFeatureFlags(),
    };
}

export { cache as _featureFlagCache };
